import React, { useState } from "react";
import {
  User,
  Search,
  Home,
} from "lucide-react";
import seniorsData from "../data/seniors.json";
import roadmapsData from "../data/roadmaps.json";
import { Senior, Roadmap, FeedbackMessage, UserPreferences, UserBadges } from "../types";
import HomePage from "./pages/HomePage";
import MyInfoPage from "./pages/MyInfoPage";
import RecommendationPage from "./pages/RecommendationPage";
import CareerDesignPage from "./pages/CareerDesignPage";
import ProfilePage from "./pages/ProfilePage";
import FeedbackPage from "./pages/FeedbackPage";

const FlowApp = () => {
  const [currentPage, setCurrentPage] = useState("home");
  const [selectedSenior, setSelectedSenior] = useState<Senior | null>(null);
  const [followedPaths, setFollowedPaths] = useState<number[]>([]);
  const [careerDesignStep, setCareerDesignStep] = useState(0);
  const [userPreferences, setUserPreferences] = useState<UserPreferences>({
    location: "",
    jobField: "",
    salary: "",
    benefits: [],
  });
  const [selectedSeniors, setSelectedSeniors] = useState<number[]>([]);
  const [selectedRoadmap, setSelectedRoadmap] = useState<Roadmap | null>(null);
  const [feedbackMessages, setFeedbackMessages] = useState<
    Record<number, FeedbackMessage[]>
  >({});
  const [currentChat, setCurrentChat] = useState<number | null>(null);
  const [showRecommendationResults, setShowRecommendationResults] = useState(false);

  // 이름 마스킹 함수
  const maskName = (name: string) => {
    if (name.length <= 2) return name;
    const firstChar = name.charAt(0);
    const lastChar = name.charAt(name.length - 1);
    return `${firstChar}${"*".repeat(name.length - 2)}${lastChar}`;
  };

  // JSON 파일에서 데이터 가져오기
  const seniors = seniorsData as Senior[];
  const roadmaps = roadmapsData as Roadmap[];

  // 사용자 배지 데이터
  const userBadges: UserBadges = {
    earned: ["신입 탐색자"],
    inProgress: [
      { name: "질문왕", progress: 3, total: 10, description: "질문 10개 작성" },
      {
        name: "로드맵 마스터",
        progress: 1,
        total: 5,
        description: "로드맵 단계 5개 완료",
      },
    ],
    locked: ["실무왕", "자격증왕", "네트워킹 전문가"],
  };







  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen flex flex-col">
      {/* 컨텐츠 영역 */}
      <div className="flex-1 overflow-y-auto pb-16">
        {currentPage === "home" && (
          <HomePage 
            setCurrentPage={setCurrentPage}
            setCareerDesignStep={setCareerDesignStep}
          />
        )}
        {currentPage === "recommendation" && (
          <RecommendationPage 
            seniors={seniors}
            maskName={maskName}
            setSelectedSenior={setSelectedSenior}
            setCurrentPage={setCurrentPage}
            userPreferences={userPreferences}
            setUserPreferences={setUserPreferences}
            showResults={showRecommendationResults}
            setShowResults={setShowRecommendationResults}
          />
        )}
        {currentPage === "profile" && (
          <ProfilePage 
            selectedSenior={selectedSenior}
            maskName={maskName}
            followedPaths={followedPaths}
            setFollowedPaths={setFollowedPaths}
            setCurrentPage={setCurrentPage}
            setShowRecommendationResults={setShowRecommendationResults}
          />
        )}
        {currentPage === "myinfo" && (
          <MyInfoPage 
            selectedRoadmap={selectedRoadmap}
            followedPaths={followedPaths}
            seniors={seniors}
            maskName={maskName}
            setFollowedPaths={setFollowedPaths}
            setCurrentPage={setCurrentPage}
            userBadges={userBadges}
          />
        )}
        {currentPage === "careerDesign" && (
          <CareerDesignPage 
            careerDesignStep={careerDesignStep}
            userPreferences={userPreferences}
            setUserPreferences={setUserPreferences}
            setCareerDesignStep={setCareerDesignStep}
            seniors={seniors}
            maskName={maskName}
            selectedSeniors={selectedSeniors}
            setSelectedSeniors={setSelectedSeniors}
            roadmaps={roadmaps}
            selectedRoadmap={selectedRoadmap}
            setSelectedRoadmap={setSelectedRoadmap}
            setCurrentPage={setCurrentPage}
          />
        )}
        {currentPage === "feedback" && (
          <FeedbackPage 
            seniors={seniors}
            followedPaths={followedPaths}
            maskName={maskName}
            feedbackMessages={feedbackMessages}
            setFeedbackMessages={setFeedbackMessages}
            currentChat={currentChat}
            setCurrentChat={setCurrentChat}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>

      {/* 하단 네비게이션 */}
      {currentPage !== "feedback" || !currentChat ? (
        <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-white border-t border-gray-200">
          <div className="flex justify-around py-2">
            <button
              onClick={() => setCurrentPage("home")}
              className={`flex flex-col items-center py-2 px-4 ${
                currentPage === "home" ? "text-blue-600" : "text-gray-400"
              }`}
            >
              <Home className="w-6 h-6" />
              <span className="text-xs mt-1">홈</span>
            </button>
            <button
              onClick={() => setCurrentPage("recommendation")}
              className={`flex flex-col items-center py-2 px-4 ${
                ["recommendation", "profile", "careerDesign"].includes(
                  currentPage
                )
                  ? "text-blue-600"
                  : "text-gray-400"
              }`}
            >
              <Search className="w-6 h-6" />
              <span className="text-xs mt-1">찾기</span>
            </button>
            <button
              onClick={() => setCurrentPage("myinfo")}
              className={`flex flex-col items-center py-2 px-4 ${
                currentPage === "myinfo" ? "text-blue-600" : "text-gray-400"
              }`}
            >
              <User className="w-6 h-6" />
              <span className="text-xs mt-1">내 정보</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default FlowApp;
